import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import MultiSelect, { type MultiSelectOption, type SelectId } from '../../components/MultiSelect';
import type { SchemaFieldProps } from '../types';
import './MemberGroupsField.css';

interface GroupOption extends MultiSelectOption {
  managed: boolean;
}

/**
 * Member `groups` override: plain groups are editable, managed (virtual) groups
 * are read-only chips. Managed and unknown ids are echoed back unchanged.
 */
const MemberGroupsField: React.FC<SchemaFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<GroupOption[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.groups
      .list()
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? (r.data as any[]) : [];
        setOptions(
          rows.map((g) => ({ _id: g._id ?? g.id, name: g.name ?? String(g._id ?? g.id), managed: !!g.managed })),
        );
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected: SelectId[] = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const byId = useMemo(() => new Map((options ?? []).map((o) => [String(o._id), o])), [options]);
  const plainOptions = useMemo(() => (options ?? []).filter((o) => !o.managed), [options]);
  const managedSelected = selected.map((id) => byId.get(String(id))).filter((o): o is GroupOption => !!o?.managed);
  // Ids that are not editable here (managed, or not in the caller's list) are kept as-is.
  const fixedIds = selected.filter((id) => {
    const o = byId.get(String(id));
    return !o || o.managed;
  });
  const plainSelected = selected.filter((id) => byId.get(String(id))?.managed === false);

  const handleChange = (ids: SelectId[]) => {
    const merged = [...fixedIds, ...ids].map(Number).filter(Number.isFinite);
    onChange([...new Set(merged)]);
  };

  return (
    <div className="member-groups-field">
      <MultiSelect
        id={`sf-${field.name}`}
        label={t(field.labelKey, field.name)}
        options={plainOptions}
        selectedIds={options ? plainSelected.map(String) : []}
        onChange={handleChange}
      />
      {managedSelected.length > 0 && (
        <div className="member-groups-field__managed" data-testid="member-managed-groups">
          <span className="member-groups-field__label">{t('members.managedGroupsLabel')}</span>
          <div className="member-groups-field__chips">
            {managedSelected.map((g) => (
              <Link
                key={String(g._id)}
                to={`/admin/groups/${g._id}/edit`}
                className="multi-select-chip member-groups-field__chip"
                title={t('groups.managedBadge')}
              >
                {g.name}
              </Link>
            ))}
          </div>
          <p className="member-groups-field__hint">{t('members.managedGroupsHint')}</p>
        </div>
      )}
      {error && <span className="form-error member-groups-field__error">{error}</span>}
    </div>
  );
};

export default MemberGroupsField;
